import {
  DataCatalogFileVariableName,
  ExportDataCatalogDatasetVariableName,
  RunScientificModelVariableName,
  SendToDocumentVariableName,
  DataCatalogFilesVariableName,
} from 'analysis-tool/AnalysisStepConfigVariableNames';
import {ObjectType} from 'app-core/framework/CoreObjectConstants';
import {getModelTypeConstantForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {BenchlingIconName, isValidBenchlingIcon} from 'ui-library/icons/BenchlingIcon';

/**
 * Config variable keys that are generated automatically and implicitly when a template is created, indexed
 * by step type.
 *
 * Contrast with the variables generated for DEFINE_TEMPLATE_VARIABLES steps, which are user-specified.
 */
export const SYSTEM_GENERATED_CONFIG_VARIABLE_KEYS_BY_STEP_TYPE = {
  [PipelineStepType.EXPORT_DATA_CATALOG_DATASET]: [
    ExportDataCatalogDatasetVariableName.DATASET_NAME,
    ExportDataCatalogDatasetVariableName.FOLDER_ID,
  ],
  [PipelineStepType.SEND_TO_DOCUMENT]: [
    SendToDocumentVariableName.DOCUMENT_ID,
    SendToDocumentVariableName.SECTION_INDEX,
  ],
  [PipelineStepType.DATA_CATALOG_FILE]: [DataCatalogFileVariableName.FILE_ID],
  [PipelineStepType.DATA_CATALOG_FILES]: [DataCatalogFilesVariableName.FILE_IDS],
};

type SystemGeneratedConfigVariableKey =
  (typeof SYSTEM_GENERATED_CONFIG_VARIABLE_KEYS_BY_STEP_TYPE)[keyof typeof SYSTEM_GENERATED_CONFIG_VARIABLE_KEYS_BY_STEP_TYPE][number];

/**
 * Check if a config variable keys was generated automatically by the system.
 *
 * Contrast with the variables generated for DEFINE_TEMPLATE_VARIABLES steps, which are user-specified.
 */
export function isSystemGeneratedConfigVariableKey(
  configVariableKey: string
): configVariableKey is SystemGeneratedConfigVariableKey {
  return Object.values(SYSTEM_GENERATED_CONFIG_VARIABLE_KEYS_BY_STEP_TYPE)
    .flatMap((configKeys) => configKeys as Array<string>)
    .includes(configVariableKey);
}

const CONFIG_KIND_TO_ICON: Partial<Record<PipelineConfigJsonSpec['kind'], BenchlingIconName>> = {
  string: 'text',
  number: 'number',
  integer: 'number',
  boolean: 'boolean',
  date: 'calendar',
  enum: ModelTypeConstants.SCHEMA_FIELD_SELECTOR_OPTION,
};

export function getIconForConfigVariable(variable: {
  variableTypeConfigSpec: PipelineConfigJsonSpec;
}): BenchlingIconName | null {
  const spec = variable.variableTypeConfigSpec;
  if (spec.kind === 'array') {
    return getIconForConfigVariable({variableTypeConfigSpec: spec.elementSpec});
  }
  if (spec.kind === 'domain') {
    const firstType = spec.types[0];
    if (firstType != null) {
      const modelType = getModelTypeConstantForObjectType(firstType);
      if (isValidBenchlingIcon(modelType)) {
        return modelType;
      }
    }
    return null;
  }
  return CONFIG_KIND_TO_ICON[spec.kind] ?? null;
}

/**
 * Check if a config variable should be displayed as required.
 */
export function showConfigVariableAsRequired(variable: {
  variableTypeConfigSpec: PipelineConfigJsonSpec;
}): boolean {
  const spec = variable.variableTypeConfigSpec;
  return spec.kind === 'array' ? (spec.minItems ?? 0) > 0 : spec.required;
}

/**
 * Checks if a config variable is for a specific domain object type.
 * Handles both singular domain types and arrays of domain types.
 */
export function isDomainConfigVariable(
  variable: {variableTypeConfigSpec: PipelineConfigJsonSpec},
  objectType: ObjectType
): boolean {
  const spec = variable.variableTypeConfigSpec;
  if (spec.kind === 'domain' && spec.types?.some((type) => type === objectType)) {
    return true;
  }
  if (spec.kind === 'array' && spec.elementSpec) {
    const elementSpec = spec.elementSpec;
    return elementSpec.kind === 'domain' && elementSpec.types?.some((type) => type === objectType);
  }
  return false;
}

export function isFileConfigVariable(variable: {variableTypeConfigSpec: PipelineConfigJsonSpec}): boolean {
  return isDomainConfigVariable(variable, 'FILE');
}

export function isPredictionBatchFolderConfigVariable(variable: {configVariableKey: string}): boolean {
  return variable.configVariableKey === RunScientificModelVariableName.PREDICTION_BATCH_FOLDER_ID;
}

export function isImportPlatesConfigVariable(variable: {
  configVariableKey: string;
  pipelineStepDomain?: {stepType: PipelineStepType};
}): boolean {
  return (
    variable.configVariableKey === 'scratchPlateIds' &&
    variable.pipelineStepDomain?.stepType === PipelineStepType.IMPORT_PLATES
  );
}

export function isLaunchTimeVariable(variable: {
  variableTypeConfigSpec: PipelineConfigJsonSpec;
  configVariableKey: string;
  pipelineStepDomain?: {stepType: PipelineStepType};
}): boolean {
  return isFileConfigVariable(variable) || isImportPlatesConfigVariable(variable);
}

export function getFileConfigVariables<T extends {variableTypeConfigSpec: PipelineConfigJsonSpec}>(
  configVariables: Array<T>
): Array<T> {
  return configVariables.filter((variable) => isFileConfigVariable(variable));
}

export function prepareFileVariableValue<T>(variable: {configVariableKey: string}, value: T): T | Array<T> {
  return variable.configVariableKey === 'fileIds' ? [value] : value;
}
